import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";
import { Sparkles, HardHat, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function AcceptInvite() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { acceptInvite } = useAuth();
  const [code, setCode] = useState(params.get("code") || "");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchPreview = async (c = code) => {
    if (!c.trim()) return;
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/auth/employee/invite/${c.trim()}`);
      setPreview(data);
      // Phase 5 #6 — prefill the email the admin entered, but keep it editable.
      setEmail(data.email || "");
    } catch (err) {
      setPreview(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invite not found");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (params.get("code")) fetchPreview(params.get("code"));
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await acceptInvite(code.trim(), email.trim(), password);
    setSubmitting(false);
    if (!res.ok) toast.error(res.error || "Failed");
    else { toast.success("Welcome aboard!"); navigate("/"); }
  };

  return (
    <div className="min-h-screen px-4 py-8 flex flex-col">
      <div className="flex flex-col items-center gap-3 pt-6 pb-6">
        <div className="w-16 h-16 rounded-2xl bg-indigo-600 text-white grid place-items-center shadow-lg shadow-indigo-600/30">
          <HardHat className="w-8 h-8" strokeWidth={2.5} />
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Join your team</h1>
        <p className="text-sm text-slate-500 text-center max-w-xs">Enter the code your boss shared with you</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="code">Invite Code</Label>
          <div className="flex gap-2">
            <Input id="code" data-testid="invite-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Paste your code" className="h-14 rounded-xl text-base flex-1" />
            <Button type="button" data-testid="check-invite-btn" onClick={() => fetchPreview()} disabled={loading || !code.trim()} className="h-14 rounded-xl bg-slate-900 hover:bg-slate-800 px-4">Check</Button>
          </div>
        </div>

        {preview && (
          <div data-testid="invite-preview" className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-indigo-700 mt-0.5 shrink-0" />
            <div className="text-sm">
              <div className="font-bold text-indigo-900">{preview.owner_business} has invited you to join as an employee.</div>
              <div className="text-indigo-800 mt-0.5">Hi {preview.employee_name}, you're joining as <b>{preview.role}</b>.</div>
            </div>
          </div>
        )}

        {preview && (
          <form onSubmit={onSubmit} className="space-y-3 pt-2" data-testid="accept-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Your Email</Label>
              <Input id="email" data-testid="accept-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-14 rounded-xl text-base" placeholder="you@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Set Password</Label>
              <PasswordInput id="password" data-testid="accept-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="h-14 rounded-xl text-base" placeholder="At least 6 characters" />
            </div>
            <Button type="submit" disabled={submitting} data-testid="accept-submit" className="w-full h-14 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-lg font-semibold gap-2">
              {submitting ? "Joining..." : "Join & Continue"} <ArrowRight className="w-5 h-5" />
            </Button>
          </form>
        )}
      </div>

      <div className="mt-4 text-center">
        <button onClick={() => navigate("/login")} className="text-sm text-slate-500 underline" data-testid="back-to-login">Back to login</button>
      </div>
    </div>
  );
}
